<template>
  <skeleton-plan-schedule-loader v-if="initLoading" :plan-weeks="loaderPlanWeeks" />
  <div v-else class="schedule-view">
    <!-- error state -->
    <callout v-if="error" type="error">
      <div>{{ error }}</div>
    </callout>

    <!-- drop down not in use until we build the list view -->
    <!--    <div class="form__group">-->
    <!--      <div class="select-style">-->
    <!--        <select name="schedule_view" aria-label="Select schedule view" v-model="selectedView">-->
    <!--          <option v-for="view in viewOptions" :key="view" :value="view">-->
    <!--            {{ view }}-->
    <!--          </option>-->
    <!--        </select>-->
    <!--      </div>-->
    <!--    </div>-->

    <!-- grid view -->
    <plan-week-grid
      v-if="planWeeksReady && selectedView === 'Grid'"
      :plan-weeks="planWeeks"
      :read-only="readOnly"
      @update-plan-day="updatePlanDay"
      @update-plan-day-schedule="updatePlanDaySchedule"
      @remove-plan-day-schedule="removePlanDaySchedule"
      @reorder-plan-day-schedule="reorderPlanDaySchedule"
    />

    <div v-if="planWeeksReady && isRoutine && !readOnly" class="form-actions">
      <div v-if="!deleting && hasWorkouts" class="form-action">
        <!-- actual form submit with page reload after modal confirm -->
        <form ref="publishForm" method="POST" :action="`/my/routines/${planId}/edit-status`">
          <input name="_method" type="hidden" value="PUT" />
          <input name="status" type="hidden" value="1" />
          <csrf-input />
          <submit-button
            :submitting="saving"
            label="Finish and Launch"
            submitting-label="Saving…"
            classes="-main -large-medium-only"
            data-tour="routine-finish-button"
            @submit-action="openModal('publish')"
          />
        </form>
      </div>
      <div class="form-action">
        <!-- actual form submit with page reload after modal confirm -->
        <form ref="deleteForm" method="POST" :action="`/my/routines/${planId}/edit-status`">
          <input name="_method" type="hidden" value="PUT" />
          <input name="status" type="hidden" value="0" />
          <csrf-input />
          <submit-button
            v-if="!publishing"
            :submitting="saving"
            label="Delete Draft"
            submitting-label="Deleting…"
            classes="-neutral -large-medium-only"
            @submit-action="openModal('delete')"
          />
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import _debounce from 'lodash/debounce'
import { mapState, mapActions } from 'pinia'
import { useModalsStore } from '@/stores/modals'
import { useRoutinesStore } from '@/stores/routines'
import { fbApi } from '@/utils/http-api'
import PlanWeekGrid from './plan-week-grid.vue'
import Callout from '@/components/notifications/callout.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import CsrfInput from '@/components/forms/csrf-input.vue'
import SkeletonPlanScheduleLoader from '@/components/loaders/skeleton-plan-schedule-loader.vue'

/**
 * Handles deleting and toggling the "type_id" of a plan day schedule for a routine.
 * - currently only supports grid view
 * - TODO: list view
 */
export default defineComponent({
  name: 'RoutineSchedule',
  components: { SkeletonPlanScheduleLoader, PlanWeekGrid, Callout, SubmitButton, CsrfInput },
  props: {
    planId: {
      type: Number,
      required: true,
    },
    // differentiate between plan types: programs, challenges, routines
    planCategory: {
      default: '',
      validator: (val: string) => ['', 'program', 'challenge', 'routine'].includes(val),
    },
    loaderPlanWeeks: {
      type: Number,
      default: 2,
    },

    // removes any reordering, deleting, or required toggling making
    // the view fully read only (with expand / collapse)
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // initial XHR flow (full screen loader)
      initLoading: false,
      error: '',
      planWeeks: [],

      // drop down options / model
      viewOptions: ['Grid', 'List'],
      selectedView: 'Grid',

      // status changes
      saving: false,
      deleting: false,
      publishing: false,
    }
  },
  computed: {
    ...mapState(useModalsStore, ['simpleModal']),

    planWeeksReady() {
      return !this.initLoading && !this.error && this.planWeeks
    },

    // helper to check if there are actually any workouts within planWeeks
    hasWorkouts() {
      return this.planWeeks.reduce((acc: any, planWeek: any) => {
        // if already true, just return, no need to check others
        // as workouts were found
        if (acc) {
          return acc
        }

        // check if planDaySchedule has length
        if (planWeek && Array.isArray(planWeek.days)) {
          return planWeek.days.some((day: any) => day.planDaySchedule.length > 0)
        }

        // no workouts
        return false
      }, false)
    },

    isRoutine() {
      return this.planCategory === 'routine'
    },

    deleteModalId() {
      return 'routine-delete-modal'
    },

    publishModalId() {
      return 'routine-publish-modal'
    },
  },

  watch: {
    'simpleModal.action'(newAction: string, prevAction: string) {
      // delete modal confirm
      if (
        this.simpleModal.id === this.deleteModalId &&
        newAction !== prevAction &&
        newAction === 'confirm' &&
        !this.deleting
      ) {
        this.handleModalDelete()
        return
      }

      // publish modal confirm
      if (
        this.simpleModal.id === this.publishModalId &&
        newAction !== prevAction &&
        newAction === 'confirm' &&
        !this.publishing
      ) {
        this.handleModalPublish()
      }
    },
  },

  async created() {
    await this.fetchPlanWeeks()
  },

  methods: {
    ...mapActions(useModalsStore, ['openSimpleModal']),
    ...mapActions(useRoutinesStore, ['setMetaData']),

    // this is expensive, so see if user actions stack up before doing
    /**
     * Refresh the meta data (in routine-meta-data on edit-schedule.blade.php)
     * - data linked via pinia routines module
     * - expensive operation, so debounce until the user has stopped clicking
     * - no loading state at all, behind the scenes
     */
    refreshRoutineMetaData: _debounce(async function (this: any) {
      try {
        const res = await fbApi.get(`/routines/${this.planId}/generate-meta-data`)

        if (!res || !res.body || !res.body.data) {
          throw Error('Missing plan meta data response')
        }

        this.setMetaData(res.body.data)
      } catch (e) {
        // noop
      }
    }, 2000),

    /**
     * get the schedule builder (plan_days and plan_day_schedule by week)
     */
    async fetchPlanWeeks() {
      // reset state
      this.initLoading = true
      this.error = ''

      try {
        const res = await fbApi.get(`/plans/${this.planId}/plan-weeks`)

        if (!res || !res.body || !res.body.data || !res.body.data.planWeeks) {
          throw Error('Missing plan week response')
        }

        this.planWeeks = res.body.data.planWeeks
      } catch (e) {
        this.error = 'Error fetching plan weeks'
      }

      // cancel loading
      this.initLoading = false
    },

    /**
     * Removes plan day schedule from planWeeks.
     * The schedule item itself has already been delete from db.
     * Meta data will be refreshed.
     * @param id
     */
    removePlanDaySchedule({ id }: { id: number }) {
      // not optimal triple loop :(
      // iterate week, then days, then filter planDaySchedule for deleted id
      this.planWeeks.forEach((planWeek: any) => {
        planWeek.days.forEach((day: any) => {
          if (Array.isArray(day.planDaySchedule)) {
            day.planDaySchedule = day.planDaySchedule

              // remove
              .filter((daySchedule: any) => daySchedule.id !== id)

              // reorder
              .map((daySchedule: any, index: number) => {
                daySchedule.order = index + 1
                return daySchedule
              })
          }
        })
      })

      this.refreshRoutineMetaData()
    },

    /**
     * Replaces plan day from planWeeks[].days and refreshes meta data
     *
     * @param id
     * @param planDay
     */
    updatePlanDay({ id, planDay }: { id: number; planDay: object }) {
      // not optimal double loop :(
      // iterate week, then days, then filter planDaySchedule for deleted id
      this.planWeeks.forEach((planWeek: any) => {
        // day iteration
        planWeek.days.forEach((day: any) => {
          if (Array.isArray(day.planDaySchedule)) {
            // plan day replacement / mutation
            if (day.planDay && day.planDay.id === id) {
              day.planDay = planDay
            }

            return day
          }
        })
        // end day iteration
      })

      this.refreshRoutineMetaData()
    },

    /**
     * Replaces existing plan day schedule from planWeeks[].days
     * then refreshes meta data.
     *
     * @param id
     * @param planDaySchedule
     */
    updatePlanDaySchedule({ id, planDaySchedule }: { id: number; planDaySchedule: object }) {
      // not optimal triple loop :(
      // iterate week, then days, then map planDaySchedule / replace object
      this.planWeeks.forEach((planWeek: any) => {
        // day iteration
        planWeek.days.forEach((day: any) => {
          if (Array.isArray(day.planDaySchedule)) {
            // schedule item replacement
            day.planDaySchedule = day.planDaySchedule.map((existingPlanDaySchedule: any) => {
              if (existingPlanDaySchedule.id === id) {
                return planDaySchedule
              }

              return existingPlanDaySchedule
            })
            // end replace schedule item
          }
        })
        // end day iteration
      })

      this.refreshRoutineMetaData()
    },

    /**
     * Switches position of two plan day schedule by the order field.
     * Reminder indexes are 0 based and order field is 1 based.
     *
     * EX: schedule item index 1 gets an order field of 1
     * - update order of schedule item index 0 to 2
     * - update order of schedule item index 1 to 1
     *
     */
    reorderPlanDaySchedule({
      id,
      schedulableId,
      schedulableType,
      planDayId,
      prevOrder,
      newOrder,
    }: {
      id: number
      schedulableId: number
      schedulableType: string
      planDayId: number
      prevOrder: number
      newOrder: number
    }) {
      // iterate and find the plan day to reorder
      this.planWeeks.forEach((planWeek: any) => {
        planWeek.days.forEach((day: any) => {
          // restrict to JUST the planDayId schedule items
          if (Array.isArray(day.planDaySchedule) && day.planDay && day.planDay.id === planDayId) {
            // update the order fields
            const reorderedItems = day.planDaySchedule.map((planDaySchedule: any) => {
              // update schedule item with new order and set it to the old
              if (planDaySchedule.order === newOrder) {
                return {
                  ...planDaySchedule,
                  order: prevOrder,
                }
              }

              // change order field for schedule item that was actually updated
              if (planDaySchedule.id === id) {
                return {
                  ...planDaySchedule,
                  order: newOrder,
                }
              }

              // all other schedule items
              return planDaySchedule
            })
            // end update order fields

            // sort by order
            day.planDaySchedule = reorderedItems.sort((a: any, b: any) => {
              if (a.order > b.order) {
                return 1
              } else if (a.order < b.order) {
                return -1
              }
              return 0
            })
          }
        })
        // end day iteration
      })

      // NOTE: no need to refresh meta data on reordering
    },

    // modals for status change
    openModal(modal: string) {
      // delete status change
      if (modal === 'delete') {
        this.openSimpleModal({
          type: 'CONFIRM',
          id: this.deleteModalId,
          props: {
            heading: 'Delete Routine?',
            subheading: 'Are you sure you want to delete this routine?',
            buttonText: 'Delete',
            cancelText: 'Cancel',
          },
        })
        return
      }

      // publish status change
      this.openSimpleModal({
        type: 'CONFIRM',
        id: this.publishModalId,
        props: {
          heading: 'All Finished?',
          subheading:
            'Once a routine is launched, you <strong>cannot add or delete workouts</strong>. Please make sure all edits and selected workouts are finalized before launching.  Once launched, you will be able to schedule and complete the routine!',
          buttonText: 'Launch',
          cancelText: 'Cancel',
        },
      })
    },

    handleModalDelete() {
      // delete confirmed, submit traditional form
      // saving = spinner on button
      // deleting vs publishing will show / hide the button (so user doesn't accidentally click)
      this.deleting = true
      this.saving = true

      const refsDeleteForm = this.$refs.deleteForm as HTMLFormElement

      this.$nextTick(() => {
        refsDeleteForm.submit()
      })
    },

    handleModalPublish() {
      // publish confirmed
      this.publishing = true
      this.saving = true

      const refsPublishForm = this.$refs.publishForm as HTMLFormElement

      this.$nextTick(() => {
        refsPublishForm.submit()
      })
    },
  },
})
</script>
